import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import UserCard from './UserCard';
import { Modal, Button, Form } from 'react-bootstrap';
import { InfoCircleFill, Eye, EyeSlash, EnvelopeFill, LockFill, ExclamationTriangleFill, PersonBadgeFill } from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../authSlice';
import { jwtDecode } from 'jwt-decode';

// Import your slideshow images
import slide4 from '../img/slide4.jpg';
import slide2 from '../img/slide2.jpg';
import slide3 from '../img/slide3.jpg';

const SignIn = ({ setIsAuthenticated, setUserRole }) => {
  // Form states
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [staffEmail, setStaffEmail] = useState('');
  const [staffPassword, setStaffPassword] = useState('');
  const [studentId, setStudentId] = useState('');
  const [studentPassword, setStudentPassword] = useState('');
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [rememberMeStaff, setRememberMeStaff] = useState(false);
  const [rememberMeStudent, setRememberMeStudent] = useState(false);

  // Modal states
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [showForgotPasswordError, setShowForgotPasswordError] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);

  // Password visibility
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showStaffPassword, setShowStaffPassword] = useState(false);
  const [showStudentPassword, setShowStudentPassword] = useState(false);

  // Slideshow state
  const [slides, setSlides] = useState([
    { id: 0, className: 'active', image: slide4 },
    { id: 1, className: 'next', image: slide2 },
    { id: 2, className: 'prev', image: slide3 }
  ]);

  const navigate = useNavigate();

  const dispatch = useDispatch();
  
  // Error message timeout effect
  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Forgot password message timeout effect
  useEffect(() => {
    if (forgotPasswordMessage) {
      setShowForgotPasswordError(true);
      const timer = setTimeout(() => {
        setShowForgotPasswordError(false);
        setForgotPasswordMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [forgotPasswordMessage]);

  // Slideshow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setSlides(prev => {
        const newSlides = [...prev];
        // Rotate slides
        newSlides.forEach(slide => {
          if (slide.className === 'active') slide.className = 'prev';
          else if (slide.className === 'next') slide.className = 'active';
          else if (slide.className === 'prev') slide.className = 'next';
        });
        return newSlides;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Authentication handlers with validation
  const handleSignIn = async (event, userType) => {
    event.preventDefault();
    
    // Clear previous errors
    setError('');
    setShowError(false);
  
    // Validate inputs based on user type
    let email, password;
    switch (userType) {
      case 'admin':
        email = adminEmail.trim();
        password = adminPassword.trim();
        break;
      case 'staff':
        email = staffEmail.trim();
        password = staffPassword.trim();
        break;
      case 'student':
        email = studentId.trim();
        password = studentPassword.trim();
        break;
      default:
        return;
    }
  
   // Validate email / student ID
if (!email) {
  setError(userType === 'student' ? 'Please enter your Student ID' : 'Please enter a valid email address');
  return;
}
if (userType !== 'student' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
  setError('Please enter a valid email address');
  return;
}
  
    // Validate password exists
    if (!password) {
      setError('Please enter your password');
      return;
    }
  
    try {
      let response;
      let userData = {};
      
      switch (userType) {
        case 'admin':
          response = await authAPI.adminSignIn({ email, password });
          if (response.data.success) {
            const decoded = jwtDecode(response.data.token);
            userData = {
              role: 'admin',
              email: decoded.email,
              name: decoded.name || response.data.userData?.name || 'Admin',
            };
          }
          break;
        case 'staff':
           response = await authAPI.staffSignIn({ email, password });
          if (response.data.success) {
            const decoded = jwtDecode(response.data.token);
            userData = {
              role: 'staff',
              email: decoded.email,
              name: decoded.name || response.data.userData?.name || 'Staff Member',
            };
          }
          break;
       case 'student':
  response = await authAPI.studentSignIn({ studentIdNumber: studentId.trim(), password });
  if (response.data.success) {
    const decoded = jwtDecode(response.data.token);
    userData = {
      role: decoded.role,
      id: decoded.id,
      class: decoded.class,
      studentIdNumber: decoded.studentIdNumber,
      email: decoded.email,
      name: decoded.name || response.data.userData?.name || 'Student',
    };
  }
  break;
        default:
          return;
      }
  
      if (response.data.success) {
        // Dispatch credentials to Redux store
        dispatch(setCredentials({
          user: userData,
          token: response.data.token
        }));
  
        setIsAuthenticated(true);
        setUserRole(userType);
        
        // Always store these values in localStorage
        localStorage.setItem('userRole', userType);
        localStorage.setItem('token', response.data.token); // Main token storage
        localStorage.setItem('userData', JSON.stringify(userData));
        
        // Additional storage for "Remember Me" functionality
        if ((userType === 'admin' && rememberMe) || 
            (userType === 'staff' && rememberMeStaff) || 
            (userType === 'student' && rememberMeStudent)) {
          localStorage.setItem(`${userType}Token`, response.data.token);
        } else {
          // Clear the type-specific token if "Remember Me" is not checked
          localStorage.removeItem(`${userType}Token`);
        }
  
        navigate('/home');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
    }
  };
  // Password reset handler with validation
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = forgotPasswordEmail.trim();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setForgotPasswordMessage('Please enter a valid email address');
      return;
    }

    try {
  const response = await authAPI.forgotPassword({ email });
      setForgotPasswordMessage(response.data.message);
    } catch (err) {
      setForgotPasswordMessage(err.response?.data?.message || 'Error sending password reset email');
    }
  };

  // Modal handlers
  const handleClose = () => {
    setShowForgotPasswordModal(false);
    setShowAdminModal(false);
    setShowStaffModal(false);
    setShowStudentModal(false);
  };

  const handleOpenAdminModal = () => {
    setError('');
    setShowError(false);
    setShowAdminModal(true);
  };

  const handleOpenStaffModal = () => {
    setError('');
    setShowError(false);
    setShowStaffModal(true);
  };

  const handleOpenStudentModal = () => {
    setError('');
    setShowError(false);
    setShowStudentModal(true);
  };

  // Password visibility togglers
  const toggleAdminPassword = () => setShowAdminPassword(!showAdminPassword);
  const toggleStaffPassword = () => setShowStaffPassword(!showStaffPassword);
  const toggleStudentPassword = () => setShowStudentPassword(!showStudentPassword);

  // Token validation effects
  useEffect(() => {
    const validateToken = async (token, role) => {
      try {
        await authAPI.validateToken(token);
        setIsAuthenticated(true);
        setUserRole(role);
        navigate('/home');
      } catch (error) {
        localStorage.removeItem(`${role}Token`);
      }
    };

    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) validateToken(adminToken, 'admin');

    const staffToken = localStorage.getItem('staffToken');
    if (staffToken) validateToken(staffToken, 'staff');

    const studentToken = localStorage.getItem('studentToken');
    if (studentToken) validateToken(studentToken, 'student');
  }, [setIsAuthenticated, setUserRole, navigate]);

  return (
    <div className="signin-container d-flex flex-column min-vh-100 overflow-hidden">
      {/* Slideshow Background */}
      <div className="slideshow position-fixed w-100 h-100">
        {slides.map((slide) => (
          <div 
            key={slide.id}
            className={`slide ${slide.className}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}
      </div>

      {/* Content Overlay */}
      <div className="content-overlay flex-grow-1 d-flex flex-column overflow-y-auto">
        <div className="container my-auto py-4">
          <div className="user-card-wrapper">
            <div className="user-card-container">
              <div className="row g-3 justify-content-center">
                <div className="col-md-4 col-sm-6">
                  <UserCard 
                    name="Admin" 
                    text="Login as Admin" 
                    icon="admin" 
                    onClick={handleOpenAdminModal} 
                  />
                </div>
                <div className="col-md-4 col-sm-6">
                  <UserCard 
                    name="Staff" 
                    text="Login as Staff" 
                    icon="staff" 
                    onClick={handleOpenStaffModal} 
                  />
                </div>
                <div className="col-md-4 col-sm-6">
                  <UserCard 
                    name="Student" 
                    text="Login as Student" 
                    icon="student" 
                    onClick={handleOpenStudentModal} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Login Modal */}
      <Modal show={showAdminModal} onHide={handleClose} centered className="futuristic-modal">
        <Modal.Header closeButton>
          <Modal.Title>ADMIN PORTAL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => handleSignIn(e, 'admin')}>
            <Form.Group controlId="admin-email" className="futuristic-input-group">
              <EnvelopeFill className="input-icon futuristic-icon" />
              <Form.Control 
                className={`form-control futuristic-input ${adminEmail ? 'filled' : ''}`}
                type="text"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
              />
              <Form.Label className="futuristic-label">Email Address</Form.Label>
            </Form.Group>

            <Form.Group controlId="admin-password" className="futuristic-input-group">
              <div className="password-input">
                <LockFill className="input-icon futuristic-icon" />
                <Form.Control
                  className={`form-control futuristic-input ${adminPassword ? 'filled' : ''}`}
                  type={showAdminPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                />
                <Form.Label className="futuristic-label">Password</Form.Label>
                <Button 
                  variant="link" 
                  onClick={toggleAdminPassword}
                  className="futuristic-eye-btn"
                >
                  {showAdminPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </div>
            </Form.Group>
            
            <Form.Group controlId="remember-me" className="d-flex align-items-center mb-4">
              <label className="futuristic-toggle me-2">
                <input 
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <span className="futuristic-toggle-label">Remember me</span>
              <span className="futuristic-link ms-auto" onClick={() => {
                setShowAdminModal(false);
                setShowForgotPasswordModal(true);
              }}>
                Forgot password?
              </span>
            </Form.Group>
            
            {showError && error && (
              <p className="futuristic-error" style={{ animation: 'fadeOut 0.5s ease 2.5s forwards' }}> <ExclamationTriangleFill className='mx-1'/>
                {error}
              </p>
            )}
            
            <Modal.Footer className="d-flex flex-column">
              <Button className='futuristic-btn' type="submit">
                ACCESS SYSTEM
              </Button>
              <p className="text-center mt-3 mb-0">
                <span className="text-white">Need an account? </span>
                <Link to="/signup" className="futuristic-link">
                  Request Access
                </Link>
              </p>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal show={showForgotPasswordModal} onHide={handleClose} centered className="futuristic-modal">
        <Modal.Header closeButton>
          <Modal.Title>PASSWORD RECOVERY</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleForgotPassword}>
            <Form.Group controlId="forgot-password-email" className="futuristic-input-group">
              <EnvelopeFill className="input-icon futuristic-icon" />
              <Form.Control 
                className={`form-control futuristic-input ${forgotPasswordEmail ? 'filled' : ''}`}
                type="text"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
              />
              <Form.Label className="futuristic-label">Registered Email</Form.Label>
            </Form.Group>
            
            {showForgotPasswordError && forgotPasswordMessage && (
              <p className={forgotPasswordMessage.includes('success') ? 'futuristic-success' : 'futuristic-error'}>
                <InfoCircleFill className="me-2" />
                {forgotPasswordMessage}
              </p>
            )}
            
            <Modal.Footer className="d-flex flex-column">
              <Button className='futuristic-btn' type="submit">
                SEND RECOVERY LINK
              </Button>
              <Button 
                variant="link" 
                className="futuristic-link mt-3"
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setShowAdminModal(true);
                }}
              >
                Back to Login
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Staff Login Modal */}
      <Modal show={showStaffModal} onHide={handleClose} centered className="futuristic-modal">
        <Modal.Header closeButton>
          <Modal.Title>STAFF PORTAL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => handleSignIn(e, 'staff')}>
            <Form.Group controlId="staff-email" className="futuristic-input-group">
              <EnvelopeFill className="input-icon futuristic-icon" />
              <Form.Control 
                className={`form-control futuristic-input ${staffEmail ? 'filled' : ''}`}
                type="text"
                value={staffEmail}
                onChange={(e) => setStaffEmail(e.target.value)}
              />
              <Form.Label className="futuristic-label">Email Address</Form.Label>
            </Form.Group>

            <Form.Group controlId="staff-password" className="futuristic-input-group">
              <div className="password-input">
                <LockFill className="input-icon futuristic-icon" />
                <Form.Control
                  className={`form-control futuristic-input ${staffPassword ? 'filled' : ''}`}
                  type={showStaffPassword ? 'text' : 'password'}
                  value={staffPassword}
                  onChange={(e) => setStaffPassword(e.target.value)}
                />
                <Form.Label className="futuristic-label">Password</Form.Label>
                <Button 
                  variant="link" 
                  onClick={toggleStaffPassword}
                  className="futuristic-eye-btn"
                >
                  {showStaffPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </div>
            </Form.Group>
            
            <Form.Group controlId="remember-me-staff" className="d-flex align-items-center mb-4">
              <label className="futuristic-toggle me-2">
                <input 
                  type="checkbox"
                  checked={rememberMeStaff}
                  onChange={(e) => setRememberMeStaff(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <span className="futuristic-toggle-label">Remember me</span>
            </Form.Group>
            
            {showError && error && (
              <p className="futuristic-error" style={{ animation: 'fadeOut 0.5s ease 2.5s forwards' }}><ExclamationTriangleFill className='mx-1'/>
                {error}
              </p>
            )}
            
            <Modal.Footer className="d-flex flex-column">
              <Button className='futuristic-btn' type="submit">
                ACCESS SYSTEM
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Student Login Modal */}
      <Modal show={showStudentModal} onHide={handleClose} centered className="futuristic-modal">
        <Modal.Header closeButton>
          <Modal.Title>STUDENT PORTAL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => handleSignIn(e, 'student')}>
          <Form.Group controlId="student-id" className="futuristic-input-group">
            <PersonBadgeFill className="input-icon futuristic-icon" />
            <Form.Control
              className={`form-control futuristic-input ${studentId ? 'filled' : ''}`}
              type="text"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
            />
            <Form.Label className="futuristic-label">Student ID</Form.Label>
          </Form.Group>

            <Form.Group controlId="student-password" className="futuristic-input-group">
              <div className="password-input">
                <LockFill className="input-icon futuristic-icon" />
                <Form.Control
                  className={`form-control futuristic-input ${studentPassword ? 'filled' : ''}`}
                  type={showStudentPassword ? 'text' : 'password'}
                  value={studentPassword}
                  onChange={(e) => setStudentPassword(e.target.value)}
                />
                <Form.Label className="futuristic-label">Password</Form.Label>
                <Button 
                  variant="link" 
                  onClick={toggleStudentPassword}
                  className="futuristic-eye-btn"
                >
                  {showStudentPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </div>
            </Form.Group>
            
            <Form.Group controlId="remember-me-student" className="d-flex align-items-center mb-4">
              <label className="futuristic-toggle me-2">
                <input 
                  type="checkbox"
                  checked={rememberMeStudent}
                  onChange={(e) => setRememberMeStudent(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <span className="futuristic-toggle-label">Remember me</span>
            </Form.Group>
            
            {showError && error && (
              <p className="futuristic-error" style={{ animation: 'fadeOut 0.5s ease 2.5s forwards' }}><ExclamationTriangleFill className='mx-1'/>
                {error}
              </p>
            )}
            
            <Modal.Footer className="d-flex flex-column">
              <Button className='futuristic-btn' type="submit">
                ACCESS SYSTEM
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SignIn;